import PropTypes from 'prop-types';
import React from 'react';
import { Grid } from 'semantic-ui-react';
import { Link } from 'gatsby-plugin-intl';
import { useTranslate } from 'react-polyglot';

import Config from '../../../config';
import ClassCardFlip from '../ClassCard/ClassCardFlip';
import Container from '../../layout/Container';
import FeaturedClass from '../FeaturedClass/FeaturedClass';
import { injectUser } from '../../session';
import { mobileDetect } from '../../../utils/browser';
import { translateLocal } from '../../../i18n';

import './ClassesBlock.scss';
import SectionTitle from '../SectionTitle/SectionTitle';

const ClassesBlock = ({
  authUser, id, data, pageData, inverseBackground, transparent, withLinks, withTopPadding, withTitles, flipCards, zoomable, columns, limit, animated, plusButtonTarget, disablePreSaleLink, withRating, ratings, onRatingSubmit, withFeatured,
}) => {

  const t = useTranslate();

  const { isMobile } = mobileDetect();

  const itemsArray = [];

  const itemsUnfiltered = isMobile() ? data.items.filter((item) => !!item.name) : data.items.filter((item) => !!item.name && (!withFeatured || !item.featuredCard));
  const items = itemsUnfiltered.slice(0, limit || itemsUnfiltered.length);

  const showPlusButton = itemsUnfiltered.length > limit;

  const featuredItem = withFeatured && data.items.find((item) => item.name && item.featuredCard);

  while (items.length > 0) {
    itemsArray.push(items.splice(0, columns));
  }

  return (
    <div className={`courses-block ${transparent ? 'transparent' : ''} ${inverseBackground ? 'inverse-background' : ''} ${withTopPadding ? 'top-padding' : ''}`} id={id}>
      <Container>

        {withTitles && (
          <SectionTitle
            title={pageData ? translateLocal(pageData.title) : translateLocal(data.title)}
            subtitle={pageData ? translateLocal(pageData.subtitle) : translateLocal(data.subtitle)}
          />
        )}

        {featuredItem && (
          <FeaturedClass item={featuredItem} />
        )}

        <Grid columns={columns} stackable>
          {itemsArray.map((itemArray, itemsArrayIndex) => (
            <Grid.Row className="course-row" key={itemsArrayIndex}>
              {itemArray.map((item, index) => {

                const hasAccess = authUser && (authUser.isFullPassActive || (authUser.classes && authUser.classes.available.indexOf(item.id) > -1));

                return (
                  <Grid.Column className="class-col" key={index}>
                    <ClassCardFlip
                      flip={flipCards}
                      image={{ src: item.teaserImage ? item.teaserImage.childImageSharp.fluid : null, alt: item.image ? translateLocal(item.image.alt) : null }}
                      /* srcSet={[
                        { selector: '2x', image: AyaImage2x },
                        { selector: '3x', image: AyaImage3x },
                      ]} */
                      name={item.name}
                      domain={translateLocal(item.domain)}
                      teaches={translateLocal(item.teaches)}
                      description={translateLocal(item.description)}
                      link={withLinks && ((!disablePreSaleLink || !hasAccess) || !item.isPreSale) && `${hasAccess && !item.isPreSale ? Config.routePaths.program : Config.routePaths.classes}/${item.id}`}
                      zoomable={zoomable}
                      animated={animated}
                      preSale={item.isPreSale}
                      preSaleLabel={item.preSaleLabel}
                      withRating={withRating && !item.isPreSale}
                      rating={ratings[item.id]}
                      onRatingSubmit={(value, cb) => onRatingSubmit(value, cb, item.id)}
                      isNew={item.isNew}
                    />
                  </Grid.Column>
                );
              })}
            </Grid.Row>
          ))}
        </Grid>

        {!!plusButtonTarget && showPlusButton && (
          <div className="actions">
            <Link to={plusButtonTarget} className="button sm bordered">
              {t('common.discoverAllClasses')}
            </Link>
          </div>
        )}
      </Container>
    </div>
  );
};

ClassesBlock.propTypes = {
  authUser: PropTypes.object,
  data: PropTypes.object.isRequired,
  disablePreSaleLink: PropTypes.bool,
  flipCards: PropTypes.bool,
  id: PropTypes.string,
  pageData: PropTypes.object,
  inverseBackground: PropTypes.bool,
  transparent: PropTypes.bool,
  withLinks: PropTypes.bool,
  withTitles: PropTypes.bool,
  withTopPadding: PropTypes.bool,
  zoomable: PropTypes.bool,
  animated: PropTypes.bool,
  plusButtonTarget: PropTypes.string,
  columns: PropTypes.number,
  limit: PropTypes.number,
  withRating: PropTypes.bool,
  ratings: PropTypes.object,
  onRatingSubmit: PropTypes.func,
  withFeatured: PropTypes.bool,
};

ClassesBlock.defaultProps = {
  authUser: null,
  disablePreSaleLink: false,
  flipCards: false,
  pageData: null,
  id: 'classes',
  inverseBackground: false,
  transparent: false,
  withLinks: false,
  withTitles: true,
  withTopPadding: false,
  zoomable: true,
  animated: true,
  plusButtonTarget: null,
  columns: 2,
  limit: null,
  withRating: false,
  ratings: {},
  onRatingSubmit: () => true,
  withFeatured: true,
};

export default injectUser(
  ClassesBlock,
);
